import {
  DEFAULT_PER_PAGE,
  UserSystemSettings,
} from '@/core/interfaces/UserSystemSettings';

const USER_SYSTEM_SETTINGS_KEY = 'user_system_settings';
const LEGACY_YEAR_KEY = 'selected_year';
const CURRENT_USER_KEY = 'current_user';

const getStoredUser = () => {
  try {
    const userData = localStorage.getItem(CURRENT_USER_KEY);
    if (!userData) {
      return null;
    }

    return JSON.parse(userData);
  } catch {
    return null;
  }
};

const getSettingsStore = (): Record<string, UserSystemSettings> => {
  try {
    const stored = localStorage.getItem(USER_SYSTEM_SETTINGS_KEY);
    if (!stored) {
      return {};
    }

    return JSON.parse(stored) as Record<string, UserSystemSettings>;
  } catch {
    return {};
  }
};

const persistSettingsStore = (
  store: Record<string, UserSystemSettings>,
): void => {
  localStorage.setItem(USER_SYSTEM_SETTINGS_KEY, JSON.stringify(store));
};

export const getCurrentUserId = (): number | null => {
  const user = getStoredUser();
  return user?.id ?? null;
};

export const getDefaultUserSystemSettings = (
  idDonVi: number,
  year?: string,
): UserSystemSettings => ({
  year: year || new Date().getFullYear().toString(),
  idDonVi,
  perpage: DEFAULT_PER_PAGE,
});

export const getUserSystemSettings = (
  userId: number,
): UserSystemSettings | null => {
  const store = getSettingsStore();
  return store[userId.toString()] ?? null;
};

export const saveUserSystemSettings = (
  userId: number,
  settings: UserSystemSettings,
): void => {
  const store = getSettingsStore();
  store[userId.toString()] = settings;
  persistSettingsStore(store);

  localStorage.setItem(LEGACY_YEAR_KEY, settings.year);
};

export const resolveUserSystemSettings = (
  userId: number,
  idDonVi: number,
  capDonVi: number,
  yearOverride?: string,
): UserSystemSettings => {
  const stored = getUserSystemSettings(userId);
  const legacyYear = localStorage.getItem(LEGACY_YEAR_KEY);
  const defaults = getDefaultUserSystemSettings(
    idDonVi,
    yearOverride || legacyYear || undefined,
  );

  const settings: UserSystemSettings = stored
    ? { ...defaults, ...stored }
    : defaults;

  if (capDonVi !== 1) {
    settings.idDonVi = idDonVi;
  }

  saveUserSystemSettings(userId, settings);
  return settings;
};

export const getYearFromUserSettings = (): string => {
  const userId = getCurrentUserId();
  if (userId) {
    const settings = getUserSystemSettings(userId);
    if (settings?.year) {
      return settings.year;
    }
  }

  return (
    localStorage.getItem(LEGACY_YEAR_KEY) ||
    new Date().getFullYear().toString()
  );
};

export const getIdDonViFromUserSettings = (): number | null => {
  const user = getStoredUser();
  if (!user) {
    return null;
  }

  const userId = user.id;
  const settings = userId ? getUserSystemSettings(userId) : null;

  if (settings?.idDonVi) {
    return settings.idDonVi;
  }

  return user.id_don_vi ?? user.don_vi?.id ?? null;
};

export const updatePerPageInUserSettings = (perpage: number): void => {
  const userId = getCurrentUserId();
  if (!userId) {
    return;
  }

  const existing = getUserSystemSettings(userId);
  if (!existing) {
    return;
  }

  saveUserSystemSettings(userId, { ...existing, perpage });
};

export const getPerPageFromUserSettings = (): number => {
  const userId = getCurrentUserId();
  if (userId) {
    const settings = getUserSystemSettings(userId);
    if (settings?.perpage) {
      return settings.perpage;
    }
  }

  return DEFAULT_PER_PAGE;
};
